/* 템플릿 팝업 — 1단계: 모든 페이지 미리보기 → 2단계: 사진 고르는 방식 → (현장 목록) → 3단계: 사진 고르기
   사진 공간은 인디포토 공간 태그. 태그가 없으면 AI가 공간을 추정하고(주황 표시), 사장님이 눌러서 고칠 수 있다(인디포토에 저장). */
import { IcAi } from './icons';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, ChoiceCard, EmptyState, FilterChip, Input, Modal, Skeleton, Tag } from '@indipia/ui-core';
import { showcaseApi, errorMessage } from '../api/showcaseApi';
import { generateFromTemplate } from '../ai/agent';
import { ROOM_LABEL, SPACE_ORDER, STYLES, roomLabel } from '../model/constants';
import {
  CAT_LABEL, TEMPLATES, aiTextCount, buildTemplatePages, makeBuildCtx, pageKindLabel, previewPages, templateSlots, type PhotoMeta, type Template,
} from '../model/templates';
import type { ShowcasePhoto, ShowcaseSite } from '../model/types';
import { toast, useEd } from '../store/editor';
import { patchPhotoSpace, refreshSitesIfEmpty, photoState, spaceOf, srcOf } from '../store/photos';
import { MineToggle, NoPhotos } from './Common';
import { PageThumb } from './render';
import { Thumb } from './SidePanel';
import { cardPress, chipRow, cx, dialogBox, footMeta, modeList, popNote, popover, shake, textOk, textWarn } from './ui';

type Step = 'preview' | 'mode' | 'sites' | 'picker';
interface PickPhoto { url: string; room: string; siteId: number; siteName: string; p: ShowcasePhoto }
interface Pick {
  mode: 'site' | 'all';
  siteId: number | null;
  photos: PickPhoto[];
  slots: Record<string, number>;
  selected: Set<string>;
  notice: string;
  touched: boolean;
  /** reasons·mains 는 공간별 — mains 는 AI 추천으로 대표가 된 사진 url(이 사진이 그대로 1번일 때만 'AI 대표') */
  ai: { state: '' | 'run' | 'done' | 'none'; reasons: Record<string, string>; mains: Record<string, string>; done: number; total: number };
  userMain: Record<string, string>;
  filter: string;
  title: string;
  classifying: number;
}

const siteLabel = (s: ShowcaseSite) => s.siteName || `현장 ${s.siteId}`;
function photosOf(sites: ShowcaseSite[]): PickPhoto[] {
  return sites.flatMap((s) => s.photos.filter((p) => p.externalId).map((p) => ({ url: srcOf(p), room: spaceOf(p), siteId: s.siteId, siteName: siteLabel(s), p })));
}

export function TemplateModal({ tpl: tpl0, onClose }: { tpl: Template; onClose: () => void }) {
  useEd();
  const [tpl, setTpl] = useState(tpl0);
  const [step, setStep] = useState<Step>('preview');
  const pickRef = useRef<Pick | null>(null);
  const [, redraw] = useReducer((x: number) => x + 1, 0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [spaceMenu, setSpaceMenu] = useState<{ ph: PickPhoto; left: number; top: number } | null>(null);
  useEffect(() => { void refreshSitesIfEmpty(); }, []);
  useEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [step]);
  const sites = photoState.sites || [];
  const pick = pickRef.current;

  /* ── 사진 고르기 상태 ── */
  const slotOf = (room: string) => (pick?.slots[room]) || 0;
  const roomOfUrl = () => new Map(pick!.photos.map((p) => [p.url, p.room]));
  const pickedCount = (room: string) => { const m = roomOfUrl(); return [...pick!.selected].filter((u) => m.get(u) === room).length; };
  const pickedGrouped = () => {
    const byUrl = new Map(pick!.photos.map((p) => [p.url, p])), g: Record<string, string[]> = {};
    pick!.selected.forEach((u) => { const p = byUrl.get(u); if (p) (g[p.room] ||= []).push(u); });
    return g;
  };
  const mainUrls = () => {
    const m = roomOfUrl(), seen = new Set<string>(), out = new Set<string>();
    pick!.selected.forEach((u) => { const r = m.get(u)!; if (!seen.has(r)) { seen.add(r); out.add(u); } });
    return out;
  };
  const roomsOfPick = () => SPACE_ORDER.filter((r) => pick!.photos.some((p) => p.room === r));

  function startPicker(mode: 'site' | 'all', siteId?: number) {
    const all = photoState.sites || []; // 최신 목록 — "내 현장만 보기"를 바꾼 직후에도
    const src = mode === 'site' ? all.filter((s) => s.siteId === siteId) : all;
    const photos = photosOf(src);
    const st = tpl.style ? STYLES[tpl.style] : null;
    pickRef.current = {
      mode, siteId: siteId ?? null, photos, slots: {}, selected: new Set(), notice: '', touched: false,
      ai: { state: '', reasons: {}, mains: {}, done: 0, total: 0 }, userMain: {},
      filter: mode === 'all' && tpl.focus ? tpl.focus : 'all',
      title: mode === 'site' ? siteLabel(src[0]) : st ? `${st.label} 무드 컬렉션` : `${CAT_LABEL[tpl.cat]} 베스트 컬렉션`,
      classifying: 0,
    };
    resetSlots(true);
    setStep('picker');
    redraw(); // 이미 사진 고르기 화면이면(내 현장만 보기를 바꿨을 때) step 이 그대로라 직접 다시 그린다
    void classifyMissing().then(() => { if (pickRef.current?.mode === 'site') void aiRank(); });
  }
  /** 템플릿의 공간별 칸 수 — 현장별·처음이면 공간마다 칸 수만큼 앞에서부터 골라 둔다 */
  function resetSlots(prefill: boolean) {
    const P = pickRef.current!;
    const rooms = SPACE_ORDER.filter((r) => P.photos.some((p) => p.room === r));
    P.slots = templateSlots(tpl, rooms);
    if (prefill && P.mode === 'site' && !P.touched) {
      P.selected = new Set();
      rooms.forEach((r) => P.photos.filter((p) => p.room === r).slice(0, P.slots[r]).forEach((p) => P.selected.add(p.url)));
    } else {
      // 공간이 바뀐 사진 때문에 칸을 넘친 공간은 뒤에서부터 뺀다
      const m = new Map(P.photos.map((p) => [p.url, p.room])), cnt: Record<string, number> = {};
      P.selected = new Set([...P.selected].filter((u) => { const r = m.get(u)!; cnt[r] = (cnt[r] || 0) + 1; return cnt[r] <= (P.slots[r] || 0); }));
    }
  }
  /** 공간 태그가 없는 사진 — AI가 공간을 추정한다(이미 추정한 사진은 서버 저장값) */
  async function classifyMissing() {
    const P = pickRef.current;
    if (!P) return;
    const todo = P.photos.filter((x) => x.p.spaceSource === 'NONE' && !x.p.spaceTag);
    if (!todo.length) return;
    P.classifying = todo.length;
    redraw();
    for (let i = 0; i < todo.length; i += 24) {
      const batch = todo.slice(i, i + 24);
      try {
        const res = await showcaseApi.classify(batch.map((x) => ({ photoId: x.p.photoId, externalId: x.p.externalId })));
        res.forEach((g) => patchPhotoSpace(g.photoId, g.spaceTag, g.spaceName, 'GUESS'));
      } catch (e) {
        toast('사진 공간을 알아보지 못했어요 — ' + errorMessage(e));
        break;
      }
      if (pickRef.current !== P) return;
      P.photos.forEach((x) => { x.room = spaceOf(x.p); });
      P.classifying = Math.max(0, todo.length - i - batch.length);
      resetSlots(!P.touched);
      redraw();
    }
    P.classifying = 0;
    redraw();
  }
  /** 사진 공간 고치기 — 인디포토에 사용자 지정 공간 태그로 저장 */
  async function setSpace(ph: PickPhoto, code: string) {
    setSpaceMenu(null);
    try {
      const g = await showcaseApi.updateSpace(ph.p.photoId, code);
      patchPhotoSpace(ph.p.photoId, g.spaceTag, g.spaceName, 'USER');
      const P = pickRef.current;
      if (!P) return;
      P.photos.forEach((x) => { x.room = spaceOf(x.p); });
      resetSlots(false);
      toast(`이 사진을 '${roomLabel(code)}'(으)로 저장했어요 — 인디포토에도 반영돼요`);
      redraw();
    } catch (e) { toast('공간을 저장하지 못했어요 — ' + errorMessage(e)); }
  }

  /* ── AI 대표 사진 추천 ── 현장별(손대기 전): 그 공간 사진 전부 중 추천 순서대로 칸 수만큼 → 1번이 대표 / 그 뒤: 고른 사진 순서만 */
  async function aiRank() {
    const my = pickRef.current;
    if (!my) return;
    const fresh = my.mode === 'site' && !my.touched;
    const rooms = SPACE_ORDER.filter((r) => my.slots[r] && my.photos.some((p) => p.room === r));
    const jobs = rooms.map((r) => ({
      room: r,
      cands: fresh ? my.photos.filter((p) => p.room === r) : my.photos.filter((p) => my.selected.has(p.url) && p.room === r),
    })).filter((j) => j.cands.length >= 2);
    if (!jobs.length) { my.ai = { state: 'none', reasons: {}, mains: {}, done: 0, total: 0 }; redraw(); return; }
    my.ai = { state: 'run', reasons: {}, mains: {}, done: 0, total: jobs.length };
    my.notice = '';
    redraw();
    await Promise.all(jobs.map(async (j) => {
      try {
        const r = await showcaseApi.rank({ siteId: my.siteId, spaceTag: j.room, photos: j.cands.map((c) => ({ photoId: c.p.photoId, externalId: c.p.externalId })) });
        if (pickRef.current !== my) return;
        const ranked = r.order.map((i) => j.cands[i]?.url).filter((u): u is string => !!u);
        if (fresh && my.touched) return; // 추천이 오기 전에 직접 고르기 시작했으면 손대지 않는다
        const own = my.userMain[j.room];
        if (own && my.selected.has(own)) { applyRanking(j.room, [own, ...ranked.filter((u) => u !== own)], false); return; }
        applyRanking(j.room, ranked, fresh);
        my.ai.reasons[j.room] = r.reason;
        const top = ranked.find((u) => my.selected.has(u));
        if (top) my.ai.mains[j.room] = top;
      } catch { if (pickRef.current === my) my.ai.reasons[j.room] = ''; }
      if (pickRef.current !== my) return;
      my.ai.done++;
      redraw();
    }));
    if (pickRef.current !== my) return;
    my.ai.state = 'done';
    redraw();
  }
  function applyRanking(room: string, ranked: string[], replace: boolean) {
    const P = pickRef.current!, m = roomOfUrl();
    const keep = replace ? ranked.slice(0, P.slots[room] || 0) : ranked.filter((u) => P.selected.has(u));
    const next = new Set<string>();
    let placed = false;
    P.selected.forEach((u) => {
      if (m.get(u) !== room) { next.add(u); return; }
      if (!placed) { keep.forEach((k) => next.add(k)); placed = true; }
    });
    if (!placed) keep.forEach((k) => next.add(k));
    P.selected = next;
  }
  function togglePick(ph: PickPhoto) {
    const P = pickRef.current!;
    P.notice = '';
    if (P.selected.has(ph.url)) { P.selected.delete(ph.url); if (P.userMain[ph.room] === ph.url) delete P.userMain[ph.room]; return true; }
    const cap = slotOf(ph.room), label = roomLabel(ph.room);
    if (!cap) { P.notice = `'${tpl.name}' 템플릿에는 ${label} 사진이 들어갈 칸이 없어요.`; return false; }
    if (pickedCount(ph.room) >= cap) { P.notice = `${label} 칸이 다 찼어요 (${cap}장). 이 사진을 넣으려면 다른 ${label} 사진을 먼저 빼 주세요.`; return false; }
    P.selected.add(ph.url);
    return true;
  }
  function setMain(ph: PickPhoto) {
    const P = pickRef.current!, cap = slotOf(ph.room), label = roomLabel(ph.room);
    P.notice = '';
    if (!cap) { P.notice = `'${tpl.name}' 템플릿에는 ${label} 사진이 들어갈 칸이 없어요.`; return; }
    const m = roomOfUrl();
    const mine = [...P.selected].filter((u) => m.get(u) === ph.room && u !== ph.url);
    if (!P.selected.has(ph.url) && mine.length >= cap) { const dropped = mine.pop()!; P.selected.delete(dropped); P.notice = `${label} 칸이 다 차서 맨 뒤 사진 1장을 빼고 이 사진을 대표로 넣었어요.`; }
    P.selected.add(ph.url);
    applyRanking(ph.room, [ph.url, ...mine], false);
    P.userMain[ph.room] = ph.url;
    P.touched = true;
  }
  /** 사진 고르기에서 템플릿 바꾸기 — 고른 사진·순서(대표)는 그대로, 새 템플릿의 칸 수보다 많으면 뒤에서부터 뺀다 */
  function switchTemplate(id: string) {
    const t = TEMPLATES.find((x) => x.id === id);
    const P = pickRef.current;
    if (!t || !P) return;
    setTpl(t);
    const rooms = SPACE_ORDER.filter((r) => P.photos.some((p) => p.room === r));
    P.slots = templateSlots(t, rooms);
    const m = roomOfUrl(), cnt: Record<string, number> = {};
    P.selected = new Set([...P.selected].filter((u) => { const r = m.get(u)!; cnt[r] = (cnt[r] || 0) + 1; return cnt[r] <= (P.slots[r] || 0); }));
    if (t.focus) P.filter = t.focus;
    P.notice = '';
    toast(`'${t.name}' 템플릿으로 바꿨어요 — 고른 사진은 그대로예요`);
    redraw();
  }
  function go() {
    const P = pickRef.current!;
    const grouped = pickedGrouped();
    const byUrl = new Map(P.photos.map((p) => [p.url, p]));
    const meta = new Map<string, PhotoMeta>();
    const siteIds = new Set<number>();
    P.selected.forEach((u) => { const p = byUrl.get(u); if (p) { meta.set(u, { photoId: p.p.photoId, externalId: p.p.externalId || undefined, siteId: p.siteId }); siteIds.add(p.siteId); } });
    const site = (P.title || '').trim() || (P.mode === 'site' ? sites.find((s) => s.siteId === P.siteId)?.siteName || '우리집' : '시공사례 모음');
    const t = tpl;
    onClose();
    void generateFromTemplate(t, { grouped, site, siteId: siteIds.size === 1 ? [...siteIds][0] : null, collection: siteIds.size > 1, meta });
  }

  /* ── 그리기 ── */
  const back = () => {
    if (step === 'picker') setStep(pick?.mode === 'site' ? 'sites' : 'mode');
    else if (step === 'sites') setStep('mode');
    else setStep('preview');
  };
  // 사진 범위 — 기본은 우리 회사 모든 현장, "내 현장만 보기"를 켜면 내가 담당자·관리자인 현장만
  const scopeBar = (
    <div className="mb-05 flex items-center justify-between gap-sm">
      <p className="m-0 text-body-sm text-text-secondary">{photoState.mine ? '내가 담당자·관리자인 현장의 사진만 보여요.' : '우리 회사 모든 현장의 사진이에요.'}</p>
      <MineToggle />
    </div>
  );
  const mineEmpty = <EmptyState title="내 현장에 올린 사진이 없어요" description="내가 담당자·관리자인 현장에 올린 사진이 없어요. 내 현장만 보기를 끄면 우리 회사 모든 현장의 사진을 볼 수 있어요." />;
  let head = { name: tpl.name, desc: '' as React.ReactNode };
  let body: React.ReactNode = null, foot: React.ReactNode = null;
  if (step === 'preview') {
    const list = previewPages(tpl);
    // 분류(주방·욕실·스타일 이름)와 같은 태그는 한 번만
    const kind = tpl.style ? STYLES[tpl.style].label : CAT_LABEL[tpl.cat];
    const tags = [tpl.style ? `스타일 · ${kind}` : kind, ...tpl.tags.filter((x) => x !== kind)];
    head = { name: tpl.name, desc: <>{tpl.desc}<span className="mt-03 flex flex-wrap gap-xs">{tags.map((x) => <Tag key={x} size="sm">{x}</Tag>)}</span></> };
    body = <div className="grid grid-cols-[repeat(auto-fill,calc(var(--spacing-13)+var(--spacing-10)+var(--spacing-06)))] justify-center gap-lg">{list.map((p, i) => <figure key={p.id} className="m-0 [&_.page-thumb]:rounded-sm [&_.page-thumb]:border [&_.page-thumb]:border-border [&_.page-thumb]:shadow-[var(--elevation-1)]"><figcaption className="mb-03 text-caption font-medium text-text-secondary">{i + 1}페이지 · {pageKindLabel(p)}</figcaption><PageThumb page={p} width={248} /></figure>)}</div>;
    foot = (<>
      <p className={footMeta}>미리보기 {list.length}페이지 · {tpl.focus ? `${roomLabel(tpl.focus)} 사진으로만 채워요` : '공간 수에 따라 공간 페이지가 늘거나 줄어요'}<br />
        사진은 인디포토 공간 태그대로 들어가고, 글 {aiTextCount(list)}곳은 AI가 사장님 말투로 씁니다.</p>
      <Button size="lg" onClick={() => setStep('mode')}>이 템플릿 맞춤 편집하기</Button>
    </>);
  } else if (step === 'mode') {
    const total = sites.reduce((n, s) => n + s.photos.length, 0);
    head = { name: '사진을 어떻게 고를까요?', desc: `'${tpl.name}' 템플릿에 넣을 사진을 고르면, 고른 사진만 공간 태그대로 배치하고 AI가 글을 채워요.` };
    body = !photoState.sites ? <div className={modeList} aria-busy="true"><Skeleton variant="block" height={200} /><Skeleton variant="block" height={200} /></div>
      : !sites.length ? (photoState.mine ? <>{scopeBar}{mineEmpty}</> : <NoPhotos onLeave={onClose} />)
      : (<>
        {scopeBar}
        <div className={modeList}>
          <ChoiceCard onClick={() => setStep('sites')} title="현장별 사진 고르기"
            icon={<svg width="32" height="32" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden><path d="M3 17V6l7-3 7 3v11" /><path d="M8 17v-4h4v4M7 8h1M12 8h1M7 11h1M12 11h1" /></svg>}
            description="현장 하나를 고른 뒤, 그 현장 사진 중에서 넣을 사진을 골라요. 한 현장의 시공사례를 만들 때." hint={`현장 ${sites.length}곳`} />
          <ChoiceCard onClick={() => startPicker('all')} title="전체 사진 고르기"
            icon={<svg width="32" height="32" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden><rect x="3" y="3" width="6" height="6" rx="1.5" /><rect x="11" y="3" width="6" height="6" rx="1.5" /><rect x="3" y="11" width="6" height="6" rx="1.5" /><rect x="11" y="11" width="6" height="6" rx="1.5" /></svg>}
            description="모든 현장의 사진을 한 번에 보면서 자유롭게 골라 모아요. 컨셉·스타일별 모음집을 만들 때." hint={`사진 ${total}장`} />
        </div>
      </>);
    foot = <p className={footMeta}>템플릿: <b>{tpl.name}</b>{tpl.focus ? ` · ${roomLabel(tpl.focus)} 사진이 1장 이상 있어야 해요` : ''}</p>;
  } else if (step === 'sites') {
    head = { name: '어느 현장의 사진을 쓸까요?', desc: '현장을 고르면 템플릿의 사진 칸 수만큼 공간별로 골라 둔 채로 열려요. 바꾸고 싶은 사진만 빼고 다시 고르세요.' };
    body = (<>
      {scopeBar}
      {!sites.length && mineEmpty}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing-13)+var(--spacing-12)+var(--spacing-08)),1fr))] gap-md">
        {sites.map((s) => {
          const g: Record<string, number> = {};
          s.photos.forEach((p) => { const r = spaceOf(p); g[r] = (g[r] || 0) + 1; });
          const untagged = s.photos.filter((p) => p.spaceSource === 'NONE').length;
          const counts = SPACE_ORDER.filter((r) => g[r] && r !== 'ETC').map((r) => `${ROOM_LABEL[r]} ${g[r]}`).join(' · ');
          // 공간 태그가 없는 사진은 AI가 공간을 알아본 뒤에 판단한다 — 미리 막지 않는다
          const missing = !!tpl.focus && !g[tpl.focus] && !untagged;
          const shots = (tpl.focus && g[tpl.focus] ? s.photos.filter((p) => spaceOf(p) === tpl.focus) : s.photos).slice(0, 4);
          const open = () => { if (!missing) startPicker('site', s.siteId); };
          return (
            <Card key={s.siteId} className={cx('flex items-center [--card-thumb-aspect:auto] [&_.thumb]:shrink-0 [&_.thumb]:bg-transparent [&_.thumb]:p-04 [&_.body]:flex [&_.body]:flex-col [&_.body]:items-start [&_.body]:gap-xs [&_.body]:py-04 [&_.body]:pl-0 [&_.body]:pr-04 [&_h4]:m-0 [&_h4]:text-heading-sm',
                missing ? 'cursor-not-allowed opacity-60 shadow-none' : cardPress)} role="button" tabIndex={missing ? -1 : 0} aria-disabled={missing || undefined}
              onClick={open} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } }}
              thumb={<span className="grid grid-cols-2 gap-02 [&>*]:block [&>*]:size-08 [&>*]:rounded-sm [&>*]:bg-surface [&>img]:object-cover">{[0, 1, 2, 3].map((i) => shots[i] ? <Thumb key={i} src={srcOf(shots[i])} /> : <i key={i} />)}</span>}>
              <h4>{siteLabel(s)}</h4>
              <p>사진 {s.photos.length}장{counts ? ` · ${counts}` : ''}{untagged ? ` · 공간 미지정 ${untagged}` : ''}</p>
              {missing ? <Badge status="warning">{roomLabel(tpl.focus)} 사진이 없어서 이 템플릿은 못 써요</Badge> : <span className="text-caption font-semibold text-group">사진 고르기 ›</span>}
            </Card>
          );
        })}
      </div>
    </>);
    foot = <p className={footMeta}>템플릿: <b>{tpl.name}</b> · 다음 단계에서 사진을 고를 수 있어요.</p>;
  } else if (pick) {
    const rooms = roomsOfPick();
    const slotTxt = rooms.filter((r) => slotOf(r)).map((r) => `${roomLabel(r)} ${slotOf(r)}장`).join(' · ');
    head = {
      name: pick.mode === 'site' ? `${pick.title} — 넣을 사진 고르기` : '전체 사진에서 고르기',
      desc: `이 템플릿의 사진 칸: ${slotTxt || '없음'}. 공간마다 1번 사진이 대표 사진이에요 — 사진에 마우스를 올려 '대표로'를 누르면 바꿀 수 있어요. 공간 이름을 누르면 공간을 고칠 수 있어요.`,
    };
    const totalCap = rooms.reduce((n, r) => n + slotOf(r), 0);
    const visible = pick.photos.filter((p) => pick.filter === 'all' || p.room === pick.filter);
    const order = [...pick.selected], mains = mainUrls();
    const allOn = visible.length > 0 && visible.every((p) => pick.selected.has(p.url));
    const groups: [string, PickPhoto[]][] = pick.mode === 'all'
      ? sites.map((s) => [siteLabel(s), visible.filter((p) => p.siteId === s.siteId)] as [string, PickPhoto[]]).filter(([, l]) => l.length)
      : [[pick.title, visible]];
    const running = pick.ai.state === 'run';
    body = (<>
      <div className="sticky -top-06 z-sticky mb-04 flex flex-wrap items-center justify-between gap-sm bg-surface py-03">
        <div className={chipRow} role="group" aria-label="공간">
          {[['all', '전체'] as const, ...rooms.map((r) => [r, roomLabel(r)] as const)].map(([k, l]) => {
            const cnt = k === 'all' ? `${pick.selected.size}/${totalCap}` : slotOf(k) ? `${pickedCount(k)}/${slotOf(k)}` : '칸 없음';
            const full = k !== 'all' && slotOf(k) > 0 && pickedCount(k) >= slotOf(k);
            return (
              <FilterChip key={k} active={pick.filter === k}
                className={k !== 'all' && !slotOf(k) ? 'opacity-60' : undefined}
                onClick={() => { pick.filter = k; redraw(); }}>{l} <span className={cx('text-caption', full && pick.filter !== k ? cx(textOk, 'font-semibold') : 'opacity-80')}>{cnt}{full ? ' ✓' : ''}</span></FilterChip>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          {/* 전체 사진 고르기에서 바꾸면 그 범위의 사진으로 다시 연다(현장별은 현장이 이미 정해져 있어서 없음) */}
          {pick.mode === 'all' && <MineToggle onChange={() => startPicker('all')} />}
          <Button size="sm" variant="etc" disabled={running || pick.classifying > 0} title="AI가 공간마다 사진을 보고 대표 사진(1번)과 순서를 정해요" onClick={() => void aiRank()}>
            <IcAi size={16} />{running ? `대표 사진 고르는 중… (${pick.ai.done}/${pick.ai.total})` : 'AI 대표 사진 추천'}
          </Button>
          <Button size="sm" variant="etc" onClick={() => {
            pick.notice = ''; pick.touched = true;
            visible.forEach((p) => {
              if (!allOn) { if (!pick.selected.has(p.url) && pickedCount(p.room) < slotOf(p.room)) pick.selected.add(p.url); }
              else pick.selected.delete(p.url);
            });
            redraw();
          }}>{allOn ? '보이는 사진 모두 해제' : '보이는 사진 모두 선택'}</Button>
        </div>
      </div>
      {groups.length ? groups.map(([name, l]) => (
        <section key={name}>
          {pick.mode === 'all' && <h3 className="mb-03 mt-05 text-heading-sm">{name} <span className="ml-02 text-body-sm font-normal text-text-secondary">{l.length}장</span></h3>}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing-12)+var(--spacing-07)),1fr))] gap-sm">
            {l.map((ph) => {
              const n = order.indexOf(ph.url) + 1, isMain = mains.has(ph.url);
              const blocked = !n && (!slotOf(ph.room) || pickedCount(ph.room) >= slotOf(ph.room));
              // AI 추천 이유는 AI가 대표로 둔 그 사진이 지금도 1번일 때만 — 빼서 다음 사진이 대표가 되면 그냥 '대표'
              const own = pick.userMain[ph.room] === ph.url, why = !own && pick.ai.mains[ph.room] === ph.url && pick.ai.reasons[ph.room];
              const guess = ph.p.spaceSource === 'GUESS' || ph.p.spaceSource === 'NONE';
              const toggle = (el: Element) => {
                if (!togglePick(ph)) { shake(el); redraw(); return; }
                pick.touched = true; // 직접 고르기 시작하면 AI 추천이 늦게 와도 고른 사진을 바꾸지 않는다
                redraw();
              };
              return (
                <div key={ph.url} role="checkbox" tabIndex={0} aria-checked={!!n} aria-label={`${ph.siteName} · ${roomLabel(ph.room)}${n ? ` · ${n}번` : ''}`}
                  className={cx('group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-md border-thick border-solid bg-surface-raised',
                    '[&>img]:absolute [&>img]:inset-0 [&>img]:size-full [&>img]:object-cover',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-group',
                    n ? 'border-group' : 'border-transparent hover:border-[var(--neutral-200)] [&>img]:opacity-75',
                    blocked && 'opacity-60 [&>img]:grayscale')}
                  title={isMain ? (why ?`AI 추천 대표 사진 — ${why}` : `${roomLabel(ph.room)} 대표 사진`) : `${ph.siteName} · ${roomLabel(ph.room)}`}
                  onClick={(ev) => toggle(ev.currentTarget)} onKeyDown={(ev) => { if ((ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); toggle(ev.currentTarget); } }}>
                  <Thumb src={ph.url} />
                  <FilterChip size="sm" className={cx('absolute bottom-02 left-02 [&_svg]:size-[var(--icon-sm)]', guess ? 'border-[var(--status-warning-200)] bg-[var(--status-warning-50)] text-[var(--status-warning-700)]' : 'bg-surface-raised')} title={guess ? 'AI가 추정한 공간이에요 — 눌러서 확인·수정' : '눌러서 공간 수정'}
                    onKeyDown={(ev) => ev.stopPropagation()}
                    onClick={(ev) => { ev.stopPropagation(); const r = ev.currentTarget.getBoundingClientRect(); setSpaceMenu({ ph, left: Math.min(window.innerWidth - 270, r.left), top: Math.min(window.innerHeight - 200, r.bottom + 4) }); }}>
                    {guess && <IcAi size={16} />}{roomLabel(ph.room)}
                  </FilterChip>
                  {!!n && <Badge status="accent" className="absolute right-02 top-02">{n}</Badge>}
                  {isMain && <Badge status="info" className="absolute left-02 top-02">{why ? 'AI 대표' : '대표'}</Badge>}
                  {slotOf(ph.room) > 0 && !isMain && (
                    <FilterChip size="sm" className="absolute bottom-02 right-02 bg-surface-raised opacity-0 group-focus-within:opacity-100 group-hover:opacity-100" onKeyDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => { ev.stopPropagation(); setMain(ph); redraw(); }}>대표로</FilterChip>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )) : <EmptyState title="이 공간 사진이 없어요" />}
    </>);
    foot = <PickerFoot tpl={tpl} pick={pick} grouped={pickedGrouped()} slotOf={slotOf} onTitle={(t) => { pick.title = t; redraw(); }}
      onSwitch={switchTemplate} onGo={go} />;
  }
  return (
    <Modal open size="xl" className={dialogBox + ' max-w-page-lg'} title={head.name} onClose={() => { if (spaceMenu) setSpaceMenu(null); else onClose(); }}>
      <div className="flex items-start gap-sm border-b border-border px-06 py-04">
        {step !== 'preview' && <Button size="sm" variant="etc" onClick={back}>← 이전</Button>}
        <div className="min-w-0 flex-1 text-body-sm leading-relaxed text-text-secondary">{head.desc}</div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto bg-surface p-06" ref={bodyRef}>{body}</div>
      <footer className="flex items-center justify-between gap-md border-t border-border bg-surface-raised px-06 py-05">{foot}</footer>
      {spaceMenu && (
        <SpaceMenu at={spaceMenu} onPick={(code) => void setSpace(spaceMenu.ph, code)} onClose={() => setSpaceMenu(null)} />
      )}
    </Modal>
  );
}

function SpaceMenu({ at, onPick, onClose }: { at: { ph: PickPhoto; left: number; top: number }; onPick: (code: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', down, true);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => document.removeEventListener('mousedown', down, true);
  }, [onClose]);
  const src = at.ph.p.spaceSource;
  return (
    <div ref={ref} className={cx(popover, 'w-[calc(var(--spacing-13)+var(--spacing-11)+var(--spacing-05))]')} role="menu" style={{ left: at.left, top: at.top }}>
      <p className={popNote}>{src === 'GUESS' ? 'AI가 추정한 공간이에요. 맞으면 그대로 누르고, 틀리면 바른 공간을 눌러 주세요.' : src === 'NONE' ? '이 사진의 공간을 골라 주세요.' : '이 사진의 공간을 바꿔요.'} 고른 공간은 인디포토에도 저장돼요.</p>
      <div className={chipRow}>
        {SPACE_ORDER.map((c) => <FilterChip key={c} role="menuitemradio" aria-checked={at.ph.room === c} active={at.ph.room === c} onClick={() => onPick(c)}>{ROOM_LABEL[c]}</FilterChip>)}
      </div>
    </div>
  );
}

function PickerFoot({ tpl, pick, grouped, slotOf, onTitle, onSwitch, onGo }: {
  tpl: Template; pick: Pick; grouped: Record<string, string[]>; slotOf: (r: string) => number;
  onTitle: (t: string) => void; onSwitch: (id: string) => void; onGo: () => void;
}) {
  const n = pick.selected.size;
  let problem = '';
  if (pick.classifying) problem = `AI가 공간 태그가 없는 사진 ${pick.classifying}장의 공간을 알아보는 중이에요…`;
  else if (!n) problem = '사진을 1장 이상 골라주세요.';
  else if (tpl.focus && !grouped[tpl.focus]) problem = `이 템플릿은 ${roomLabel(tpl.focus)} 사진이 필요해요. ${roomLabel(tpl.focus)} 사진을 1장 이상 골라주세요.`;
  const built = useMemo(() => (problem ? [] : buildTemplatePages(tpl, makeBuildCtx(grouped, pick.title || '우리집', false))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [problem, tpl, JSON.stringify(grouped), pick.title]);
  const picked = SPACE_ORDER.filter((r) => grouped[r]);
  const short = picked.filter((r) => grouped[r].length < slotOf(r)).map((r) => `${roomLabel(r)} ${slotOf(r) - grouped[r].length}장`);
  const totalCap = picked.reduce((s, r) => s + slotOf(r), 0);
  const alts = !problem && !tpl.focus && picked.length === 1 ? TEMPLATES.filter((x) => x.focus === picked[0]) : [];
  return (<>
    <div className={cx(footMeta, 'flex flex-col gap-sm')} aria-live="polite">
      <div>
        {problem ? <span className={pick.classifying ? 'text-text-secondary' : undefined}>{problem}</span> : (<>
          <b>{n}장</b> 선택 ({picked.map((r) => `${roomLabel(r)} ${grouped[r].length}/${slotOf(r)}`).join(' · ')}) → {built.length}페이지 · AI 글 {aiTextCount(built)}곳
          {short.length ? <><br /><span className="text-text-secondary">빈 칸: {short.join(' · ')} — 채우지 않으면 그 공간은 사진이 적은 구성으로 만들어져요</span></>
            : n === totalCap ? <><br /><span className={textOk}>✓ 고른 공간의 사진 칸을 모두 채웠어요</span></> : null}
        </>)}
        {/* 전체시공·스타일 템플릿인데 한 공간 사진만 골랐으면: 그 공간 템플릿을 권하고, 그대로 쓰면 어떻게 되는지 알려준다 */}
        {!problem && !tpl.focus && picked.length === 1 && (
          <Alert kind="warning" className="mt-03" title={`${roomLabel(picked[0])} 사진만 골랐어요.`}
            action={alts.length ? <span className={chipRow}>{alts.map((x) => <Button key={x.id} size="sm" variant="secondary" onClick={() => onSwitch(x.id)}>{x.name}로 바꾸기</Button>)}</span> : undefined}>
            {alts.length ? `${roomLabel(picked[0])} 전용 템플릿이 더 잘 맞아요. ` : ''}
            '{tpl.name}' 그대로 만들면 {roomLabel(picked[0])} 페이지만 만들고, 소개 글도 고른 {roomLabel(picked[0])} 사진으로만 써요.
          </Alert>
        )}
        {pick.ai.state === 'run' && <><br /><span className="text-text-secondary">AI가 공간마다 사진을 보고 대표 사진을 고르는 중이에요… ({pick.ai.done}/{pick.ai.total})</span></>}
        {pick.ai.state === 'done' && <><br /><span className={textOk}>✓ AI가 공간마다 대표 사진을 골라 1번에 놓았어요 — 대표 사진에 마우스를 올리면 이유가 보여요</span></>}
        {pick.notice && <><br /><span className={textWarn}>⚠ {pick.notice}</span></>}
      </div>
      <Input size="sm" className="max-w-[calc(var(--spacing-13)+var(--spacing-11))]" label="제목에 쓸 이름" value={pick.title} maxLength={30} onChange={(e) => onTitle(e.target.value)} />
    </div>
    <Button size="lg" disabled={!!problem} onClick={onGo}>선택한 사진으로 만들기</Button>
  </>);
}
